import {CheckCircle} from '@mui/icons-material';
import {List, ListItem, ListItemText, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import muiStyled from '@mui/material/styles/styled';
import {func, string} from 'prop-types';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';

import {APP_ROUTES, FONT_FAMILIES} from '../../const';
import usePayment from '../../hooks/providers/usePayment';
import drivnSymbol from '../../img/drivn/symbol-dark.png';
import {getFrenchFormattedDate} from '../../utils';
import ErrorComponent from '../error/ErrorComponent';
import Button from '../form/buttons/Button/Button';
import Link from '../link/Link';
import Spinner from '../spinner/Spinner';

const MembershipLogo = ({productImageUrl = '', onLoad}) => {
  const {hasMembership} = usePayment();
  const backgroundColor = hasMembership ? import.meta.env.VITE_MAIN_COLOR : import.meta.env.VITE_SECONDARY_COLOR;
  const style = {
    background: backgroundColor,
    borderRadius: 10,
    maxWidth: 120,
    maxHeight: 120
  };
  return <img src={hasMembership ? productImageUrl : drivnSymbol} alt="" style={style} onLoad={onLoad} />;
};

MembershipLogo.propTypes = {
  productImageUrl: string,
  onLoad: func.isRequired
};

const Title = muiStyled(Typography)(({theme}) => ({
  marginBottom: theme.spacing(2),
  fontSize: 15,
  fontWeight: 'bold',
  font: `normal normal normal 16px/25px ${FONT_FAMILIES.TITLE}`
}));

const MembershipName = muiStyled(Typography)(({theme}) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  font: 'normal normal 600 26px/40px InstrumentSans'
}));

const MembershipPrice = muiStyled(Typography)(({theme}) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontSize: 45,
  fontWeight: 'bold',
  fontFamily: FONT_FAMILIES.TITLE,
  lineHeight: 1
}));

const MembershipPriceDetails = styled.span`
  font-size: 22px;
  vertical-align: top;
`;

const MembershipDescription = muiStyled(Box)(({theme}) => ({
  marginBottom: 26,
  fontSize: 13,
  textAlign: 'center',
  padding: `0 ${theme.spacing(1)}`,
  minHeight: 38,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center'
}));

// eslint-disable-next-line complexity
const MembershipComponent = () => {
  const {subscription, calculateCurrentSubscriptionPrice, hasMembership, maxNumberOfProjects, maxReportsDataVolumeInMegaoctets, numberOfPacksSubscribed, isTrial, endTrialDate} = usePayment();

  const formattedEndTrialDate = isTrial ? getFrenchFormattedDate(endTrialDate, 'long') : '';
  const navigate = useNavigate();
  // const periodicityWord = productSubscribed?.interval === 'month' ? 'mois' : 'an';
  const periodicityWord = 'mois';
  const numberOfProjectsWord = maxNumberOfProjects > 1 ? 'connexions' : 'connexion';
  const numberOfPacksWord = numberOfPacksSubscribed > 1 ? 'Packs' : 'Pack';

  const getMembershipDescription = () => {
    if (!hasMembership) {
      return 'Pour profiter pleinement de toutes les fonctionnalités, veuillez sélectionner un abonnement.';
    }
    // const yearlyOrMonthlyWord = productSubscribed?.interval === 'month' ? 'mensualisé' : 'annualisé';
    const yearlyOrMonthlyWord = 'mensualisé';
    const formattedEndPeriodDate = subscription ? getFrenchFormattedDate(subscription?.products[0]?.current_period_end, 'long') : '';

    return (
      <span style={{lineHeight: 1.5}}>
        {subscription?.cancel_at ? (
          <Typography fontSize={12} color="error">
            Vous avez demandé l'annulation de cet abonnement. Il prendra fin le {formattedEndPeriodDate}.
          </Typography>
        ) : (
          <>
            Prochain paiement le {formattedEndPeriodDate}
            <br />
            Abonnement {yearlyOrMonthlyWord}
          </>
        )}
      </span>
    );
  };

  const getMembershipName = () => {
    return isTrial ? "Période d'essai" : 'Aucun abonnement';
  };

  const onSubscriptionButtonClick = () => {
    navigate(APP_ROUTES.subscription);
  };

  const subscriptionPrice = calculateCurrentSubscriptionPrice();
  return (
    <Grid container flexDirection="column" alignItems="center" justifyContent="space-between" sx={{height: '100%'}}>
      <Grid item display="flex" flexDirection="column" alignItems="center">
        <Title variant="h6">{hasMembership ? 'Votre formule actuelle' : 'Aucune formule choisie'}</Title>

        {hasMembership ? null : <MembershipName>{getMembershipName()}</MembershipName>}
        {!hasMembership && isTrial ? (
          <Typography fontSize={15} color="text.secondary">
            Fin le {formattedEndTrialDate}
          </Typography>
        ) : null}

        <MembershipPrice>
          {hasMembership ? subscriptionPrice / 100 : '0'}
          <MembershipPriceDetails>€ht/{periodicityWord}</MembershipPriceDetails>
        </MembershipPrice>
        <List dense sx={{mb: 3, mt: 1.5}}>
          {maxNumberOfProjects > 0 ? (
            <ListItem>
              <CheckCircle color="success" fontSize="small" sx={{mr: 2}} />
              <ListItemText primaryTypographyProps={{fontSize: 16}} primary={`${maxNumberOfProjects} ${numberOfProjectsWord}`} />
            </ListItem>
          ) : null}
          {maxReportsDataVolumeInMegaoctets > 0 ? (
            <ListItem>
              <CheckCircle color="success" fontSize="small" sx={{mr: 2}} />
              <ListItemText primaryTypographyProps={{fontSize: 16}} primary={`${maxReportsDataVolumeInMegaoctets} Mo de volume de données`} />
            </ListItem>
          ) : null}
          {numberOfPacksSubscribed > 0 ? (
            <ListItem>
              <CheckCircle color="success" fontSize="sm all" sx={{mr: 2}} />
              <ListItemText primaryTypographyProps={{fontSize: 16}} primary={`${numberOfPacksSubscribed} ${numberOfPacksWord} Premium`} />
            </ListItem>
          ) : null}
        </List>
        <MembershipDescription>{getMembershipDescription()}</MembershipDescription>
      </Grid>

      <Grid item display="flex" flexDirection="column" alignItems="center">
        <Button onClick={onSubscriptionButtonClick} size="large" variant="contained" color="secondary" sx={{mb: 1}}>
          {hasMembership ? 'Modifier' : 'Choisir'} ma formule
        </Button>

        <Link to={APP_ROUTES.bills}>
          <Button variant="text">Mes facturations</Button>
        </Link>
      </Grid>
    </Grid>
  );
};

const Membership = () => {
  const {getProductsList, getProductsListErrorMessage, productsLoading} = usePayment();

  const fetchProductsList = async () => {
    await getProductsList();
  };

  if (getProductsListErrorMessage) {
    return (
      <Box pt={2}>
        <ErrorComponent message={getProductsListErrorMessage} onClick={fetchProductsList} buttonText="Réessayer" />
      </Box>
    );
  }

  return (
    <Grid container direction="column" alignItems="center" height="100%">
      {productsLoading ? <Spinner text="Chargement de votre formule d'abonnement" isLoading /> : <MembershipComponent />}
    </Grid>
  );
};

export default Membership;
